import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ChartCard, DonutChart } from "@/components/Elements/Charts";
import ListStatisticsCardExtra from "@/components/ListStatistics/ListStatisticsCardExtra";
import {
  useListStatistics,
  useListDonutChartSelected,
} from "@/components/ListStatistics/ListStatisticsHooks";
import {
  REFERENTIAl_STATES_ENDPOINT,
  useReferentialStates,
} from "@/entities/referential/referential-states";
import { useTranslate } from "@/modules/translate";

const STATS_DATA_KEY = "projects_states";
const FILTER_KEY = "state";

const ProjectsPerStateDonutChart = ({ valueKey }) => {
  const { t } = useTranslation();
  const { translateNumber, translateCost } = useTranslate();

  const { getState } = useReferentialStates(
    REFERENTIAl_STATES_ENDPOINT.PROJECT
  );
  const isCost = useMemo(() => valueKey === "cost", [valueKey]);

  const { loading, fetching, statsData } = useListStatistics({
    statsDataKey: STATS_DATA_KEY,
    filterKey: FILTER_KEY,
  });

  const chartData = statsData
    .map(({ state, projects_count, total_cost, interventions_cost }) => {
      const stateObj = getState(state, "value");
      const value = isCost
        ? parseInt(total_cost ?? interventions_cost ?? 0)
        : projects_count;
      return stateObj
        ? { name: stateObj.value, value, color: stateObj.color }
        : undefined;
    })
    .filter(Boolean);

  const { selectedLegend, onLegendSelectChange } = useListDonutChartSelected({
    filterKey: FILTER_KEY,
    chartData,
  });

  return (
    <ChartCard
      title={t(`project:statistics.${valueKey}PerState.title`)}
      extra={<ListStatisticsCardExtra filterKey={FILTER_KEY} />}
    >
      <DonutChart
        id={isCost ? "cost-per-state" : "count-per-state"}
        loading={loading}
        fetching={fetching}
        data={chartData}
        formatTotal={
          isCost
            ? (value) => translateNumber(value, { notation: "compact" })
            : undefined
        }
        formatLabel={
          isCost
            ? ({ value }) => translateNumber(value, { notation: "compact" })
            : undefined
        }
        formatTooltipValue={(value) =>
          isCost ? translateCost(value) : translateNumber(value)
        }
        controlled={{ selectedLegend }}
        onLegendSelectChange={onLegendSelectChange}
        formatName={(stateValue) =>
          getState(stateValue, "value")?.name ?? stateValue
        }
      />
    </ChartCard>
  );
};

export default ProjectsPerStateDonutChart;
